import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Notification } from '../interfaces/notification';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly defaultDuration = '3000';

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Internal method to display a MatSnackBar notification.
   * @param notification Notification object containing all options.
   */
  private showNotification(notification: Notification): void {
    const duration = parseInt(notification.time, 10) || 3000;
    const snackBarRef = this.snackBar.open(notification.message, 'Close', {
      duration,
      panelClass: notification.color, // e.g., 'success-snackbar', 'error-snackbar'
      horizontalPosition: 'right',
      verticalPosition: 'top'
    });

    // Make the entire snackbar dismissable by clicking anywhere on it
    snackBarRef.afterOpened().subscribe(() => {
      const snackBarContainer = document.querySelector('.mat-mdc-snack-bar-container');
      if (snackBarContainer) {
        snackBarContainer.addEventListener('click', () => snackBarRef.dismiss());
      }
    });
  }

  /**
   * Displays a success notification.
   * @param message Message to show.
   * @param duration (Optional) Duration of the notification.
   */
  success(message: string, duration: string = this.defaultDuration): void {
    const notification: Notification = {
      message,
      time: duration,
      icon: 'check_circle',
      color: 'success-snackbar',
      status: 'success'
    };
    this.showNotification(notification);
  }

  /**
   * Displays an error notification.
   * @param message Message to show.
   * @param duration (Optional) Duration of the notification.
   */
  error(message: string, duration: string = this.defaultDuration): void {
    const notification: Notification = {
      message,
      time: duration,
      icon: 'error',
      color: 'error-snackbar',
      status: 'error'
    };
    this.showNotification(notification);
  }

  /**
   * Displays an info notification.
   * @param message Message to show.
   * @param duration (Optional) Duration of the notification.
   */
  info(message: string, duration: string = this.defaultDuration): void {
    const notification: Notification = {
      message,
      time: duration,
      icon: 'info',
      color: 'info-snackbar',
      status: 'info'
    };
    this.showNotification(notification);
  }
}
